import styled from "styled-components/macro";

/**
 * DESIGN_SYSTEM.md §8's top-bar chip: 36px, 6px radius, 1px border, white, with a
 * 10px secondary micro-label above a 14px value.
 *
 * The chip is drawn here rather than with `hds/TopbarChip` because the control
 * inside is an antd Select — it carries search, a virtualised list and the
 * add-new footer, none of which a plain button gives us. This is the chip's
 * chrome around antd's behaviour.
 *
 * The two rows are laid out explicitly rather than left to antd. Its Select is
 * `controlHeight` tall (48px globally, see styles/antdTheme.ts) and centres its
 * value in that box, which inside a 36px chip put the value over the label. Here
 * the label takes a fixed 12px row and the value gets the remaining 20px, so the
 * two always stack and always share the same left inset.
 *
 * antd v5 wraps its own rules in `:where()`, so these plain selectors outspecify
 * it without `!important`.
 */
export const StyledSwitcherContainer = styled.div`
	position: relative;
	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	width: 100%;
	height: var(--size-control-chip);
	padding: 5px var(--space-3) 0;
	border: var(--border-default);
	border-radius: var(--radius-md);
	background: var(--color-surface);
	min-width: 180px;
	max-width: 400px;
	transition: border-color var(--transition-base), background-color var(--transition-base);

	&:hover {
		border-color: var(--color-border-strong);
	}
	/* §9: the chip is a field, so focus turns its own border lime rather than
	   adding a ring outside it.

	   Keyboard only. :focus-within matched a mouse click too, so the chip stayed
	   lit after picking an organisation — a highlight with nothing left to say.
	   A non-searchable Select's inner input is readonly and never focus-visible
	   on click. A searchable one (the organization switcher) is a text field, so
	   a click does light it: the chip is taking typed input then, and says so.
	   Where :has() is unsupported the rule never matches,
	   which lands on the no-highlight side. */
	&:has(:focus-visible) {
		border-color: var(--focus-border);
	}

	/* One ring around the chip, not a second one around the input inside it
	   (styles/tokens.css rings everything focus-visible by default). */
	.ant-select-selection-search-input:focus-visible,
	.ant-select-selector:focus-visible {
		outline: none;
	}

	.label {
		flex: none;
		height: 13px;
		line-height: 13px;
		font-size: var(--font-size-micro);
		color: var(--color-text-secondary);
		text-align: left;
		pointer-events: none;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	/* While the list is open the chip and the list are one control, so the seam
	   between them is flat — the same treatment the "Abmessungen" filter uses. */
	&:has(.ant-select-open) {
		border-bottom-left-radius: 0;
		border-bottom-right-radius: 0;
	}

	/* The Select is the value row: 20px, no chrome of its own.
	   position:static hands its absolutely-positioned chevron back to the chip,
	   so the arrow centres on all 44px rather than on the 22px value row.

	   It spans the chip's full padding box rather than sitting inside the 12px
	   inset, because rc-select measures THIS element to size the popup: inset,
	   it made every dropdown 24px narrower than the chip it hangs from. The
	   inset moves onto the selector below, so the value still lines up with the
	   label above it.

	   It is also what puts the chevron over the selector rather than 24px past
	   its end — see the hit-area rule below. Do not reintroduce an inline
	   width: "100%" on the Select: an inline width beats this one,
	   and the chevron loses its click with it. */
	.ant-select {
		position: static;
		flex: none;
		height: 22px;
		width: calc(100% + var(--space-3) * 2);
		margin-inline-start: calc(var(--space-3) * -1);
	}

	.ant-select .ant-select-selector {
		/* Stated so the hit area below is measured from the selector and not from
		   whatever ancestor happens to be positioned. antd sets it too; this does
		   not depend on that. */
		position: relative;
		height: 22px;
		/* Left: the chip's inset. Right: only what the chevron actually occupies —
		   its 12px of ink, the 12px inset it keeps from the edge, and 4px of
		   optical gap. Reserving the icon's whole 24px BOX (see the arrow rule
		   below) spent 8px on transparent halo and stole it from the name. */
		padding: 0 calc(var(--space-3) + 12px + var(--space-1)) 0 var(--space-3);
		border: none;
		background: transparent;
		box-shadow: none;
	}

	/* antd reserves room for the arrow twice: once on the selector (above) and
	   again on the value itself, as padding-inline-end: ceil(fontSize * 1.25) =
	   18px (select/style/single.js, the show-arrow rule). Two reservations for
	   one chevron is why the name ellipsed 18px early. The extra .ant-select-single
	   is not decoration — antd's rule carries three classes, so a two-class
	   selector loses to it and the padding survives. */
	.ant-select.ant-select-single .ant-select-selection-item,
	.ant-select.ant-select-single .ant-select-selection-search,
	.ant-select.ant-select-single .ant-select-selection-placeholder {
		height: 22px;
		line-height: 22px;
		padding: 0;
		inset-inline-start: 0;
		/* §2 assigns the switcher value the navigation weight. Nothing declared
		   it, so it was inheriting body weight and the chip read flat. */
		font-weight: var(--font-weight-medium);
	}

	/* A searchable switcher (the organization switcher): the typed text has to
	   sit exactly where the value it replaces sits.

	   antd (5.2x) puts the value and the search input into a
	   .ant-select-selection-wrap, position: relative, which already sits inside
	   the selector's padding. So the search needs no inset of its own; the
	   zeroed inset above is right, and adding the selector's 12px pushed the
	   typed text 12px right (seen 2026-09-30).

	   The wrap takes its height from an ::after whose line-height is
	   controlHeight - 2 = 46px (styles/antdTheme.ts sets 48px globally), and
	   the input inside is 46px tall too. In the 22px value row that put the
	   typed text about 12px too high, over the label (seen 2026-09-30). So the
	   wrap, the search and the input are all stated as the 22px value row. The
	   .ant-select-selector step is for specificity: antd's rules carry up to
	   four classes. */
	.ant-select.ant-select-show-search .ant-select-selector .ant-select-selection-wrap {
		height: 22px;
		&::after {
			line-height: 22px;
		}
	}

	.ant-select.ant-select-show-search .ant-select-selector .ant-select-selection-search {
		top: 0;
		bottom: auto;
		height: 22px;
	}

	.ant-select.ant-select-show-search .ant-select-selector .ant-select-selection-search-input {
		height: 22px;
		line-height: 22px;
		margin: 0;
		padding: 0;
		font: inherit;
	}

	/* The chip is one control, so all 44px of it opens the list.

	   The chevron itself never takes a click. antd gives .ant-select-arrow
	   pointer-events: none but hands it back to any .anticon inside it, and our
	   suffixIcon is not one — so the whole subtree is stated here rather than left
	   to antd's exemption. The click falls through to whatever is beneath, which
	   has to be the selector.

	   That "beneath" is why the value row alone is not enough: it is only the
	   bottom 22px of the chip, and the chevron is centred on all 44. Clicks on its
	   top half were landing on the label, which passes them to the chip, which has
	   no handler: dead.

	   rc-select toggles from the SELECTOR's own mousedown, not the container's, so
	   the hit area has to belong to the selector. A transparent pseudo-element
	   stretched over the rest of the chip does that without moving anything: 18px
	   up covers the label row (13px) and the chip's top padding (5px), 2px down
	   covers the slack under the value row. Keep these in step with the two.
	   Sideways it takes the selector's full width, which only reaches under the
	   chevron because .ant-select is widened above. */
	.ant-select .ant-select-arrow,
	.ant-select .ant-select-arrow * {
		pointer-events: none;
	}

	.ant-select .ant-select-selector::before {
		content: "";
		position: absolute;
		inset: -18px 0 -2px;
	}

	/* The chevron is centred on the whole chip rather than on the 22px value row.

	   The inset is 6px, not the chip's 12px, because a 24px Lucide chevron draws
	   only 12px of ink (path "m6 9 6 6 6-6") with a 6px transparent halo either
	   side. Insetting the BOX by 12 puts the visible chevron 18px from the edge,
	   out of line with everything else in the chip; insetting it by 6 puts the
	   INK at 12, where the label and the value start. */
	.ant-select .ant-select-arrow {
		inset-inline-end: 6px;
		margin-top: 0;
		top: 50%;
		transform: translateY(-50%);
	}
`;

/** The "nothing matches" line in a searchable switcher's list. */
export const StyledSwitcherNoMatch = styled.div`
	padding: 8px 10px;
	color: var(--color-text-secondary);
`;
